// @ts-nocheck
import React from 'react';
import { OnboardRecord } from './types';
import QueueItem from './QueueItem';

interface Props {
  records: OnboardRecord[];
  loading: boolean;
  onTapRecord: (record: OnboardRecord) => void;
}

// ── Loading Skeleton ─────────────────────────────────────────────────────

const SkeletonItem: React.FC = () => (
  <div className="bg-white rounded-xl border border-gray-100 px-4 py-3.5 animate-pulse">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-gray-200 shrink-0" />
      <div className="flex-1 min-w-0 space-y-2">
        <div className="h-3.5 bg-gray-200 rounded w-28" />
        <div className="h-2.5 bg-gray-100 rounded w-16" />
      </div>
      <div className="h-3 bg-gray-100 rounded w-10" />
    </div>
  </div>
);

// ── Empty State ──────────────────────────────────────────────────────────

const EmptyState: React.FC = () => (
  <div className="text-center py-12 px-4">
    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-100 flex items-center justify-center">
      <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    </div>
    <p className="text-sm font-semibold text-gray-500">No stores queued</p>
    <p className="text-xs text-gray-400 mt-1">Paste an Instagram URL above to get started</p>
  </div>
);

// ── Section Header ───────────────────────────────────────────────────────

const SectionHeader: React.FC<{ title: string; count: number; muted?: boolean }> = ({ title, count, muted }) => (
  <div className="flex items-center justify-between px-1 mb-2">
    <h3 className={`text-xs font-bold uppercase tracking-wider ${muted ? 'text-gray-400' : 'text-gray-600'}`}>
      {title}
    </h3>
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
      muted ? 'bg-gray-100 text-gray-400' : 'bg-gray-200 text-gray-600'
    }`}>
      {count}
    </span>
  </div>
);

// ── Main QueueList ───────────────────────────────────────────────────────

const QueueList: React.FC<Props> = ({ records, loading, onTapRecord }) => {
  if (loading) {
    return (
      <div className="space-y-2 mt-6">
        <SectionHeader title="Queue" count={0} />
        <SkeletonItem />
        <SkeletonItem />
        <SkeletonItem />
      </div>
    );
  }

  if (records.length === 0) {
    return <EmptyState />;
  }

  const active = records.filter(r => !['done', 'error'].includes(r.status));
  const completed = records.filter(r => r.status === 'done');
  const errored = records.filter(r => r.status === 'error');

  return (
    <div className="mt-6 space-y-5">
      {/* Active records */}
      {active.length > 0 && (
        <div>
          <SectionHeader title="Active" count={active.length} />
          <div className="space-y-2">
            {active.map(rec => (
              <QueueItem key={rec.id} record={rec} onTap={onTapRecord} />
            ))}
          </div>
        </div>
      )}

      {/* Errored records */}
      {errored.length > 0 && (
        <div>
          <SectionHeader title="Errors" count={errored.length} />
          <div className="space-y-2">
            {errored.map(rec => (
              <QueueItem key={rec.id} record={rec} onTap={onTapRecord} />
            ))}
          </div>
        </div>
      )}

      {/* Completed records */}
      {completed.length > 0 && (
        <div>
          <SectionHeader title="Completed" count={completed.length} muted />
          <div className="space-y-2">
            {completed.map(rec => (
              <QueueItem key={rec.id} record={rec} onTap={onTapRecord} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QueueList;
